import type { AppliedFilter } from "@/components/kit";

import { FilterSelect } from "@/components/kit";
import { formatDayTime } from "@/components/sales/shared";
import {
  Attribution,
  AttributionFilters,
  AttributionOptions,
} from "@/types/attribution";

/** The ad click the visit came from, when the link had no UTMs. */
const adClick = (attribution: Attribution) =>
  attribution.fbclid
    ? "Meta click"
    : attribution.gclid || attribution.gbraid || attribution.wbraid
      ? "Google click"
      : null;

/** utm_source, then the ad click; null for an untagged visit. */
const sourceOf = (attribution: Attribution) =>
  attribution.utmSource ?? adClick(attribution);

/** Source and campaign, for a table row. Null attribution means the app, or before tracking. */
export function SourceCell({
  attribution,
}: {
  attribution: Attribution | null | undefined;
}) {
  const source = attribution && sourceOf(attribution);

  if (!source)
    return (
      <span className="text-muted">{attribution ? "Untagged" : "No data"}</span>
    );

  return (
    <div className="flex min-w-0 flex-col leading-snug">
      <span className="truncate">{source}</span>
      {attribution.utmCampaign ? (
        <span className="text-muted truncate text-xs">
          {attribution.utmCampaign}
        </span>
      ) : null}
    </div>
  );
}

/** The Source and Campaign rows of a Filters popover. */
export function AttributionFilterSelects<T extends AttributionFilters>({
  options,
  value,
  onChange,
}: {
  options: AttributionOptions | undefined;
  value: T;
  onChange: (next: T) => void;
}) {
  const toOptions = (values: string[] = []) =>
    values.map((id) => ({ id, label: id }));

  return (
    <>
      <FilterSelect
        label="Source"
        options={toOptions(options?.sources)}
        value={value.utmSource ?? null}
        onChange={(utmSource) =>
          onChange({ ...value, utmSource: utmSource ?? undefined })
        }
      />
      <FilterSelect
        label="Campaign"
        options={toOptions(options?.campaigns)}
        value={value.utmCampaign ?? null}
        onChange={(utmCampaign) =>
          onChange({ ...value, utmCampaign: utmCampaign ?? undefined })
        }
      />
    </>
  );
}

/** Chips for the attribution filters in use; ids are the filter keys. */
export const appliedAttributionFilters = (
  filters: AttributionFilters,
): AppliedFilter[] => [
  ...(filters.utmSource
    ? [{ id: "utmSource", label: "Source", value: filters.utmSource }]
    : []),
  ...(filters.utmCampaign
    ? [{ id: "utmCampaign", label: "Campaign", value: filters.utmCampaign }]
    : []),
];

/** Everything known about the visit, for a detail panel. */
export function AttributionDetails({
  attribution,
}: {
  attribution: Attribution | null | undefined;
}) {
  if (!attribution)
    return (
      <p className="text-muted text-sm">
        No data: placed in the app, or before tracking started.
      </p>
    );

  const rows = [
    ["Source", sourceOf(attribution) ?? "Untagged"],
    ["Medium", attribution.utmMedium],
    ["Campaign", attribution.utmCampaign],
    ["Ad", attribution.utmContent],
    ["Term", attribution.utmTerm],
    ["Ad click", adClick(attribution)],
    ["Landing page", attribution.landingPage],
    ["Referrer", attribution.referrer],
    [
      "Landed",
      attribution.touchedAt ? formatDayTime(attribution.touchedAt) : null,
    ],
  ].filter((row): row is [string, string] => Boolean(row[1]));

  return (
    <dl className="flex flex-col gap-2.5 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4">
          <dt className="text-muted shrink-0">{label}</dt>
          <dd className="min-w-0 truncate text-right" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
